import { PDFDocument, PDFPage, StandardFonts, rgb } from 'pdf-lib';
import { Vec3 } from 'playcanvas';

import { Events } from './events';

type CaseMetadata = {
    caseNumber?: string;
    caseName?: string;
    incidentDate?: string;
    preparedBy?: string;
    disclaimers?: string[];
};

type Bookmark = {
    id: string;
    name: string;
    note?: string;
    createdAt?: string;
    cameraPose: {
        position: [number, number, number];
        target: [number, number, number];
        fov?: number;
    };
};

type Measurement = {
    id: string;
    number?: number;
    name?: string;
    kind?: 'distance' | 'polyline' | 'area' | 'angle';
    units?: 'm' | 'ft';
    description?: string;
    points?: [number, number, number][];
    length?: number;
    area?: number;
};

type Picture = {
    id: string;
    name: string;
    exhibitId?: string;
    capturedAt?: string;
    capturedBy?: string;
    device?: string;
    tags?: string[];
    description?: string;
};

type ScreenshotCaptureResult = {
    pngBlob?: Blob;
};

type ReportGenerationOptions = {
    download?: boolean;
    screenshotScale?: 2 | 4;
    includeOverlays?: boolean;
    settleDelayMs?: number;
    settleFrames?: number;
};

type ReportGenerationResult = {
    success: boolean;
    filename: string;
    pageCount: number;
    bookmarkCount: number;
    measurementCount: number;
    evidenceCount: number;
    runtimeMs: number;
    captureTiming: {
        settleDelayMs: number;
        settleFrames: number;
        settleWaitMs: number;
        screenshotCaptureMs: number;
        perBookmarkMs: number[];
    };
};

const sanitizeFileSegment = (value: string) => {
    const cleaned = (value ?? '').trim().replace(/[^\w-]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
    return cleaned.length > 0 ? cleaned : 'case';
};

const formatTimestamp = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${year}${month}${day}-${hours}${minutes}${seconds}`;
};

const createDownload = (blob: Blob, filename: string) => {
    const href = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = href;
    anchor.download = filename;
    anchor.click();
    URL.revokeObjectURL(href);
};

const nextAnimationFrame = () => {
    return new Promise<void>((resolve) => {
        requestAnimationFrame(() => resolve());
    });
};

const wait = (delayMs: number) => {
    return new Promise<void>((resolve) => {
        window.setTimeout(() => resolve(), Math.max(0, delayMs));
    });
};

const waitForCameraSettle = async (frames: number, delayMs: number) => {
    const startedAt = performance.now();
    const frameCount = Math.max(1, frames);

    for (let i = 0; i < frameCount; i++) {
        await nextAnimationFrame();
    }

    if (delayMs > 0) {
        await wait(delayMs);
    }

    await nextAnimationFrame();
    return performance.now() - startedAt;
};

const blobToBytes = async (blob: Blob) => {
    const arrayBuffer = await blob.arrayBuffer();
    return new Uint8Array(arrayBuffer);
};

const withFallback = (value: unknown, fallback: string) => {
    if (typeof value !== 'string') {
        return fallback;
    }
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : fallback;
};

const toIsoDate = (value: unknown) => {
    if (typeof value !== 'string') {
        return '—';
    }

    const parsed = Date.parse(value);
    if (!Number.isFinite(parsed)) {
        return value;
    }

    return new Date(parsed).toISOString().slice(0, 10);
};

const isFiniteNumber = (value: unknown): value is number => {
    return typeof value === 'number' && Number.isFinite(value);
};

const computeMeasurementValue = (measurement: Measurement) => {
    const points = Array.isArray(measurement.points) ? measurement.points : [];
    const kind = measurement.kind ?? 'distance';

    if (kind === 'area') {
        if (isFiniteNumber(measurement.area)) {
            return measurement.area;
        }
        return 0;
    }

    if (kind === 'angle') {
        if (points.length !== 3) {
            return 0;
        }

        const a = points[0];
        const b = points[1];
        const c = points[2];

        const abx = a[0] - b[0];
        const aby = a[1] - b[1];
        const abz = a[2] - b[2];
        const cbx = c[0] - b[0];
        const cby = c[1] - b[1];
        const cbz = c[2] - b[2];

        const abLen = Math.sqrt(abx * abx + aby * aby + abz * abz);
        const cbLen = Math.sqrt(cbx * cbx + cby * cby + cbz * cbz);
        if (abLen <= 1e-8 || cbLen <= 1e-8) {
            return 0;
        }

        const dot = abx * cbx + aby * cby + abz * cbz;
        const cosTheta = Math.max(-1, Math.min(1, dot / (abLen * cbLen)));
        return Math.acos(cosTheta) * (180 / Math.PI);
    }

    if (isFiniteNumber(measurement.length)) {
        return measurement.length;
    }

    if (points.length < 2) {
        return 0;
    }

    let total = 0;
    for (let i = 1; i < points.length; i++) {
        const from = points[i - 1];
        const to = points[i];
        const dx = to[0] - from[0];
        const dy = to[1] - from[1];
        const dz = to[2] - from[2];
        total += Math.sqrt(dx * dx + dy * dy + dz * dz);
    }

    return total;
};

const computeMeasurementUnits = (measurement: Measurement) => {
    if (measurement.kind === 'angle') {
        return 'deg';
    }

    if (measurement.kind === 'area') {
        return measurement.units === 'ft' ? 'sq ft' : 'sq m';
    }

    return measurement.units === 'ft' ? 'ft' : 'm';
};

const drawTopText = (page: PDFPage, text: string, x: number, yTop: number, size: number, color = rgb(0, 0, 0)) => {
    const { height } = page.getSize();
    page.drawText(text, {
        x,
        y: height - yTop - size,
        size,
        color
    });
};

const drawCoverPage = (page: PDFPage, metadata: CaseMetadata) => {
    drawTopText(page, 'Forensic Scene Report', 160, 64, 28);

    const lines = [
        ['Case Name', withFallback(metadata.caseName, 'Untitled Case')],
        ['Case Number', withFallback(metadata.caseNumber, '—')],
        ['Incident Date', toIsoDate(metadata.incidentDate)],
        ['Prepared By', withFallback(metadata.preparedBy, '—')],
        ['Generated', new Date().toISOString()]
    ];

    let y = 132;
    for (const [label, value] of lines) {
        drawTopText(page, `${label}:`, 54, y, 12);
        drawTopText(page, value, 170, y, 12);
        y += 20;
    }

    drawTopText(page, 'Disclaimers', 54, y + 24, 12);

    const disclaimers = Array.isArray(metadata.disclaimers) ? metadata.disclaimers.filter(Boolean) : [];
    if (disclaimers.length === 0) {
        drawTopText(page, 'No disclaimers specified.', 54, y + 44, 11);
        return;
    }

    let lineY = y + 44;
    disclaimers.forEach((line, index) => {
        const text = `${index + 1}. ${line}`;
        const chunks = text.match(/.{1,100}/g) ?? [text];
        chunks.forEach((chunk) => {
            drawTopText(page, chunk, 54, lineY, 11);
            lineY += 14;
        });
    });
};

const drawBookmarkPage = async (
    pdf: PDFDocument,
    page: PDFPage,
    bookmark: Bookmark,
    imageBytes: Uint8Array,
    bookmarkIndex: number,
    bookmarkCount: number
) => {
    const { width: pageWidth, height: pageHeight } = page.getSize();
    const margin = 40;
    const headerBottom = 110;
    const footerHeight = 70;

    drawTopText(page, `Viewpoint ${bookmarkIndex + 1} of ${bookmarkCount}`, margin, 34, 16);
    drawTopText(page, withFallback(bookmark.name, `Bookmark ${bookmarkIndex + 1}`), margin, 58, 13);

    const captionLine = `${bookmark.id} · ${toIsoDate(bookmark.createdAt)}`;
    drawTopText(page, captionLine, margin, 82, 10, rgb(0.35, 0.35, 0.35));

    const png = await pdf.embedPng(imageBytes);
    const imageDims = png.scale(1);

    const imageHeight = Math.max(100, pageHeight - headerBottom - footerHeight - margin);
    const imageWidth = pageWidth - margin * 2;
    const fitScale = Math.min(imageWidth / imageDims.width, imageHeight / imageDims.height);
    const drawWidth = imageDims.width * fitScale;
    const drawHeight = imageDims.height * fitScale;
    const drawX = margin + (imageWidth - drawWidth) * 0.5;
    const drawY = pageHeight - headerBottom - drawHeight;

    page.drawImage(png, {
        x: drawX,
        y: drawY,
        width: drawWidth,
        height: drawHeight
    });

    const note = withFallback(bookmark.note, 'No bookmark note.');
    const chunks = (`Caption: ${note}`).match(/.{1,95}/g) ?? [`Caption: ${note}`];
    let noteY = pageHeight - footerHeight + 10;
    chunks.forEach((chunk) => {
        drawTopText(page, chunk, margin, noteY, 10);
        noteY += 12;
    });
};

const drawMeasurementsSection = (pdf: PDFDocument, measurements: Measurement[], pageWidth: number, pageHeight: number) => {
    let page = pdf.addPage([pageWidth, pageHeight]);
    drawTopText(page, 'Measurement Table', 40, 40, 16);

    const col = {
        number: 40,
        name: 84,
        kind: 250,
        value: 330,
        units: 390,
        description: 440
    };

    const drawHeader = (targetPage: PDFPage, y: number) => {
        drawTopText(targetPage, '#', col.number, y, 10);
        drawTopText(targetPage, 'Name', col.name, y, 10);
        drawTopText(targetPage, 'Type', col.kind, y, 10);
        drawTopText(targetPage, 'Value', col.value, y, 10);
        drawTopText(targetPage, 'Units', col.units, y, 10);
        drawTopText(targetPage, 'Description', col.description, y, 10);
    };

    let y = 74;
    drawHeader(page, y);
    y += 16;

    if (measurements.length === 0) {
        drawTopText(page, 'No measurements available.', 40, y, 10);
        return;
    }

    for (let i = 0; i < measurements.length; i++) {
        if (y > pageHeight - 46) {
            page = pdf.addPage([pageWidth, pageHeight]);
            y = 46;
            drawHeader(page, y);
            y += 16;
        }

        const measurement = measurements[i];
        const value = computeMeasurementValue(measurement);
        const units = computeMeasurementUnits(measurement);

        const numberText = String(measurement.number ?? i + 1);
        const nameText = withFallback(measurement.name, measurement.id);
        const typeText = withFallback(measurement.kind, 'distance');
        const valueText = Number.isFinite(value) ? value.toFixed(4) : '0.0000';
        const descText = withFallback(measurement.description, '');

        drawTopText(page, numberText, col.number, y, 10);
        drawTopText(page, nameText.slice(0, 28), col.name, y, 10);
        drawTopText(page, typeText, col.kind, y, 10);
        drawTopText(page, valueText, col.value, y, 10);
        drawTopText(page, units, col.units, y, 10);
        drawTopText(page, descText.slice(0, 32), col.description, y, 10);
        y += 14;
    }
};

const drawEvidenceSection = (pdf: PDFDocument, pictures: Picture[], pageWidth: number, pageHeight: number) => {
    let page = pdf.addPage([pageWidth, pageHeight]);
    drawTopText(page, 'Evidence Index', 40, 40, 16);

    let y = 72;
    const drawHeader = (targetPage: PDFPage, rowY: number) => {
        drawTopText(targetPage, 'Exhibit', 40, rowY, 10);
        drawTopText(targetPage, 'Name', 104, rowY, 10);
        drawTopText(targetPage, 'Captured', 252, rowY, 10);
        drawTopText(targetPage, 'By', 330, rowY, 10);
        drawTopText(targetPage, 'Device', 390, rowY, 10);
        drawTopText(targetPage, 'Tags', 470, rowY, 10);
    };

    drawHeader(page, y);
    y += 16;

    if (pictures.length === 0) {
        drawTopText(page, 'No evidence photos available.', 40, y, 10);
        return;
    }

    for (let i = 0; i < pictures.length; i++) {
        if (y > pageHeight - 40) {
            page = pdf.addPage([pageWidth, pageHeight]);
            y = 46;
            drawHeader(page, y);
            y += 16;
        }

        const picture = pictures[i];
        const exhibit = withFallback(picture.exhibitId, `EX-${String(i + 1).padStart(3, '0')}`);
        const tags = Array.isArray(picture.tags) ? picture.tags.join(', ') : '';

        drawTopText(page, exhibit.slice(0, 12), 40, y, 10);
        drawTopText(page, withFallback(picture.name, picture.id).slice(0, 26), 104, y, 10);
        drawTopText(page, toIsoDate(picture.capturedAt), 252, y, 10);
        drawTopText(page, withFallback(picture.capturedBy, '—').slice(0, 11), 330, y, 10);
        drawTopText(page, withFallback(picture.device, '—').slice(0, 13), 390, y, 10);
        drawTopText(page, tags.slice(0, 28), 470, y, 10);
        y += 14;
    }
};

const registerReportGeneratorEvents = (events: Events) => {
    const generateReport = async (options?: ReportGenerationOptions): Promise<ReportGenerationResult> => {
        const startedAt = performance.now();

        const shouldDownload = options?.download !== false;
        const screenshotScale = options?.screenshotScale === 4 ? 4 : 2;
        const includeOverlays = options?.includeOverlays !== false;
        const settleDelayMs = Math.max(0, Math.floor(options?.settleDelayMs ?? 80));
        const settleFrames = Math.max(1, Math.floor(options?.settleFrames ?? 2));

        const metadata = (events.invoke('case.metadata') ?? {}) as CaseMetadata;
        const bookmarks = ((events.invoke('bookmarks.list') ?? []) as Bookmark[])
        .filter(bookmark => !!bookmark && typeof bookmark.id === 'string' && bookmark.cameraPose?.position && bookmark.cameraPose?.target);
        const measurements = ((events.invoke('measurements.list') ?? []) as Measurement[])
        .filter(item => !!item && typeof item.id === 'string');
        const pictures = ((events.invoke('pictures.list') ?? []) as Picture[])
        .filter(item => !!item && typeof item.id === 'string');

        const originalPose = events.invoke('camera.getPose') as {
            position?: { x: number; y: number; z: number };
            target?: { x: number; y: number; z: number };
        } | undefined;
        const originalFov = events.invoke('camera.fov') as number | undefined;

        events.fire('progressStart', 'Generating PDF report…');

        try {
            const totalSteps = Math.max(1, bookmarks.length + 3);
            let completedSteps = 0;
            const updateProgress = (text: string) => {
                const progress = Math.min(99, Math.round((completedSteps / totalSteps) * 100));
                events.fire('progressUpdate', { text, progress });
            };

            updateProgress('Preparing report pages…');

            const pdf = await PDFDocument.create();
            const font = await pdf.embedFont(StandardFonts.Helvetica);
            const pageWidth = 595;
            const pageHeight = 842;

            const coverPage = pdf.addPage([pageWidth, pageHeight]);
            coverPage.setFont(font);
            drawCoverPage(coverPage, metadata);
            completedSteps += 1;
            updateProgress('Cover page ready…');

            let settleWaitMs = 0;
            let screenshotCaptureMs = 0;
            const perBookmarkMs: number[] = [];

            try {
                for (let i = 0; i < bookmarks.length; i++) {
                    const bookmark = bookmarks[i];
                    const captureStart = performance.now();

                    events.fire('camera.setPose', {
                        position: new Vec3(bookmark.cameraPose.position),
                        target: new Vec3(bookmark.cameraPose.target)
                    }, 0);

                    if (isFiniteNumber(bookmark.cameraPose.fov)) {
                        events.fire('camera.setFov', bookmark.cameraPose.fov);
                    }

                    const settleElapsed = await waitForCameraSettle(settleFrames, settleDelayMs);
                    settleWaitMs += settleElapsed;

                    let screenshot: ScreenshotCaptureResult | undefined;
                    let lastError: unknown;
                    for (let attempt = 0; attempt < 2; attempt++) {
                        try {
                            screenshot = await events.invoke('screenshot.capture', {
                                scale: screenshotScale,
                                includeOverlays,
                                download: false,
                                returnBlob: true
                            }) as ScreenshotCaptureResult;

                            if (!screenshot?.pngBlob) {
                                throw new Error('screenshot capture did not provide PNG blob data');
                            }

                            break;
                        } catch (error) {
                            lastError = error;
                            await waitForCameraSettle(1, 32);
                        }
                    }

                    if (!screenshot?.pngBlob) {
                        throw new Error(`failed to capture screenshot for bookmark '${bookmark.id}': ${(lastError as Error)?.message ?? lastError}`);
                    }

                    const imageBytes = await blobToBytes(screenshot.pngBlob);
                    const page = pdf.addPage([pageWidth, pageHeight]);
                    page.setFont(font);
                    await drawBookmarkPage(pdf, page, bookmark, imageBytes, i, bookmarks.length);

                    const captureElapsed = performance.now() - captureStart;
                    screenshotCaptureMs += captureElapsed;
                    perBookmarkMs.push(captureElapsed);

                    completedSteps += 1;
                    updateProgress(`Captured viewpoint ${i + 1} of ${bookmarks.length}…`);
                }
            } finally {
                if (originalPose?.position && originalPose?.target) {
                    events.fire('camera.setPose', {
                        position: new Vec3(originalPose.position.x, originalPose.position.y, originalPose.position.z),
                        target: new Vec3(originalPose.target.x, originalPose.target.y, originalPose.target.z)
                    }, 0);
                }

                if (isFiniteNumber(originalFov)) {
                    events.fire('camera.setFov', originalFov);
                }
            }

            drawMeasurementsSection(pdf, measurements, pageWidth, pageHeight);
            completedSteps += 1;
            updateProgress('Measurement table ready…');

            drawEvidenceSection(pdf, pictures, pageWidth, pageHeight);
            completedSteps += 1;
            updateProgress('Evidence index ready…');

            const filename = `${sanitizeFileSegment(metadata.caseNumber || metadata.caseName || 'case')}-report-${formatTimestamp(new Date())}.pdf`;
            const bytes = await pdf.save();
            const normalizedBytes = new Uint8Array(bytes);
            const blob = new Blob([normalizedBytes], { type: 'application/pdf' });

            if (shouldDownload) {
                createDownload(blob, filename);
            }

            events.fire('progressUpdate', { text: 'Report complete', progress: 100 });

            return {
                success: true,
                filename,
                pageCount: pdf.getPageCount(),
                bookmarkCount: bookmarks.length,
                measurementCount: measurements.length,
                evidenceCount: pictures.length,
                runtimeMs: performance.now() - startedAt,
                captureTiming: {
                    settleDelayMs,
                    settleFrames,
                    settleWaitMs,
                    screenshotCaptureMs,
                    perBookmarkMs
                }
            };
        } finally {
            events.fire('progressEnd');
        }
    };

    events.function('report.generate', async (options?: ReportGenerationOptions) => {
        return await generateReport(options);
    });

    events.on('report.generate', async () => {
        try {
            await generateReport({ download: true });
        } catch (error) {
            await events.invoke('showPopup', {
                type: 'error',
                header: 'Report generation failed',
                message: `'${(error as Error)?.message ?? error}'`
            });
        }
    });
};

export { registerReportGeneratorEvents };
export type { ReportGenerationOptions, ReportGenerationResult };
